import React, { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Box from "@mui/material/Box";
import Topbar from "./Topbar";
import { getDatabase, ref, update } from "firebase/database";
import { Avatar, TextField, Pagination, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Stack, TableSortLabel } from "@mui/material";



const Associations = () => {
    const [associations, setAssociations] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [usersPerPage] = useState(5);
    const [selectedUser, setSelectedUser] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [sortField, setSortField] = useState('RegisterDate');
    const [sortOrder, setSortOrder] = useState('desc');


    const fetchAssociations = async () => {
        try {
            const apiKey = 'your-api-key';
            const databaseURL = 'https://engenheirossemfronteiros-default-rtdb.europe-west1.firebasedatabase.app/';
            const endpoint = `Association.json?apiKey=${apiKey}`;

            const response = await fetch(`${databaseURL}/${endpoint}`);

            if (!response.ok) {
                throw new Error(`Error fetching Association list: ${response.statusText}`);
            }

            const associationData = await response.json();

            if (!associationData) {
                console.error('No associations found.');
                return;
            }

            const associationsList = Object.entries(associationData)
                .map(([uid, data]) => ({ uid, ...data }))
                // Só associações inativas com email verificado (ou já ativas)
                // aparecem para aprovação; pendentes sem email verificado
                // ficam ocultas.
                .filter(a => a.Enabled !== false || a.EmailVerified === true);

            const sortedAssociationsList = associationsList.sort((a, b) =>
                a.Enabled === b.Enabled ? 0 : a.Enabled ? 1 : -1
            );

            setAssociations(sortedAssociationsList);
        } catch (error) {
            console.error('Error fetching associations list:', error.message);
        }
    };

    useEffect(() => {
        fetchAssociations();
    }, []);

    const deleteAssociation = async (uid) => {
        try {
            const apiKey = 'your-api-key';
            const databaseURL = 'https://engenheirossemfronteiros-default-rtdb.europe-west1.firebasedatabase.app/';
            const endpoint = `Association/${uid}.json?apiKey=${apiKey}`;

            const response = await fetch(`${databaseURL}/${endpoint}`, {
                method: 'DELETE',
            });

            if (!response.ok) {
                throw new Error(`Error deleting association: ${response.statusText}`);
            }

            console.log(`Associação com UID ${uid} apagada com sucesso.`);
            await fetchAssociations();
        } catch (error) {
            console.error('Erro ao apagar a associação:', error.message);
        }
    };

    async function updateEnabledParameter(userID, newEnabledValue) {
        const db = getDatabase();
        const updates = {};
        updates[`/Association/${userID}/Enabled`] = newEnabledValue;

        try {
            await update(ref(db), updates);
            setAssociations((prevUsers) =>
                prevUsers.map((user) =>
                    user.uid === userID ? { ...user, Enabled: newEnabledValue } : user
                )
            );
            console.log('Enabled parameter updated successfully');
        } catch (error) {
            console.error('Error updating enabled parameter:', error.message);
        }
    }

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value.toLowerCase());
        setCurrentPage(1);
    };

    const handleSort = (field) => {
        const isAsc = sortField === field && sortOrder === 'asc';
        setSortOrder(isAsc ? 'desc' : 'asc');
        setSortField(field);
    };


        // 1. Filtrar
    const filteredAssociations = associations.filter(association =>
        association.Name?.toLowerCase().includes(searchQuery) ||
        association.Address?.toLowerCase().includes(searchQuery) ||
        association.Email?.toLowerCase().includes(searchQuery) ||
        association.NIF?.toLowerCase().includes(searchQuery)
    );

    const parseDateString = (dateStr) => {
        if (!dateStr) return 0;
        
        // Divide o texto onde houver espaços, barras ou dois pontos
        // Ex: "12/01/2026 20:41:41" vira a array ["12", "01", "2026", "20", "41", "41"]
        const parts = dateStr.trim().split(/[\s/:]+/);
        
        // Se a data não estiver completa, vai para o fim da lista
        if (parts.length < 3) return 0;
        
        // Reconstrói a data num Timestamp matemático comparável
        return new Date(
            parts[2],             // Ano
            parts[1] - 1,         // Mês (o JavaScript começa a contar os meses no 0)
            parts[0],             // Dia
            parts[3] || 0,        // Horas
            parts[4] || 0,        // Minutos
            parts[5] || 0         // Segundos
        ).getTime();
    };



    // 2. Ordenar
    const sortedAssociations = filteredAssociations.sort((a, b) => {
        // Regra 1: Contas desativadas no topo
        if (Boolean(a.Enabled) !== Boolean(b.Enabled)) {
            return a.Enabled ? 1 : -1;
        }

        // Regra 2: Ordenação pelo campo escolhido
        if (sortField === 'RegisterDate') {
            const dateA = parseDateString(a.RegisterDate);
            const dateB = parseDateString(b.RegisterDate);
            if (dateA < dateB) return sortOrder === 'asc' ? -1 : 1;
            if (dateA > dateB) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        } else {
            // Ordenação alfabética (Nome, Email, etc.)
            let valA = a[sortField] || '';
            let valB = b[sortField] || '';
            if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
            if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        }
    });

    // 3. Paginar
    const totalPages = Math.ceil(sortedAssociations.length / usersPerPage);
    const indexOfLastUser = currentPage * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
    const currentUsers = sortedAssociations.slice(indexOfFirstUser, indexOfLastUser);


    const handleChangePage = (event, newPage) => {
        setCurrentPage(newPage);
    };

    const handleOpenDialog = (association) => {
        setSelectedUser(association);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Topbar />
            <TextField
                fullWidth
                label="Pesquisar"
                variant='outlined'
                sx={{ margin: "8px", width: 'calc(100% - 16px)' }}
                onChange={handleSearchChange}
            />
            <Paper elevation={1} sx={{ margin: "0 8px" }}>
                <TableContainer component={Paper} sx={{ mt: 4, overflow: 'hidden', borderRadius: '12px', boxShadow: '0px 4px 12px rgba(0,0,0,0.1)' }}>
                    <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHead sx={{ backgroundColor: '#1C4E80' }}>
                            <TableRow>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}> </TableCell>

                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                                    <TableSortLabel
                                        active={sortField === 'Name'}
                                        direction={sortField === 'Name' ? sortOrder : 'asc'}
                                        onClick={() => handleSort('Name')}
                                        sx={{ color: 'white !important', '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                                    >
                                        Nome
                                    </TableSortLabel>
                                </TableCell>

                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Morada</TableCell>

                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                                    <TableSortLabel
                                        active={sortField === 'Email'}
                                        direction={sortField === 'Email' ? sortOrder : 'asc'}
                                        onClick={() => handleSort('Email')}
                                        sx={{ color: 'white !important', '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                                    >
                                        Email
                                    </TableSortLabel>
                                </TableCell>

                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                                    <TableSortLabel
                                        active={sortField === 'RegisterDate'}
                                        direction={sortField === 'RegisterDate' ? sortOrder : 'asc'}
                                        onClick={() => handleSort('RegisterDate')}
                                        sx={{ color: 'white !important', '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                                    >
                                        Data Registo
                                    </TableSortLabel>
                                </TableCell>

                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>NIF</TableCell>
                                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Estado</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {currentUsers.map((association) => (
                                <TableRow
                                    key={association.uid}
                                    onClick={() => handleOpenDialog(association)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <TableCell><Avatar alt={association.Email || 'N/A'} src={association.ProfileURL} /></TableCell>
                                    <TableCell>{association.Name || 'N/A'}</TableCell>
                                    <TableCell>{association.Address || 'N/A'}</TableCell>
                                    <TableCell>{association.Email || 'N/A'}</TableCell>
                                    <TableCell>{association.RegisterDate || 'N/A'}</TableCell>
                                    <TableCell>{association.NIF || 'N/A'}</TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                            {!association.Enabled && (
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        deleteAssociation(association.uid);
                                                    }}
                                                >
                                                    Apagar
                                                </Button>
                                            )}
                                            <Button
                                                variant={association.Enabled ? 'contained' : 'outlined'}
                                                color={association.Enabled ? 'success' : 'error'}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    updateEnabledParameter(association.uid, !association.Enabled);
                                                }}
                                            >
                                                {association.Enabled ? 'Ativo' : 'Ativar'}
                                            </Button>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination count={totalPages} page={currentPage} onChange={handleChangePage} sx={{ '.MuiPaginationItem-root': { borderRadius: '4px' } }} />
                </Box>
            </Paper>
            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>Detalhes</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                        <Avatar
                            alt={selectedUser?.Email || 'N/A'}
                            src={selectedUser?.ProfileURL}
                            sx={{ width: 100, height: 100, mb: 2 }}
                        />
                        <DialogContentText>
                            Nome: {selectedUser?.Name}<br />
                            Morada: {selectedUser?.Address}<br />
                            Email: {selectedUser?.Email}<br />
                            Informação: {selectedUser?.OtherInfo}<br />
                            NIF: {selectedUser?.NIF}<br />
                        </DialogContentText>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Fechar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Associations;